import { UseFormReturn, useForm, useWatch } from "react-hook-form";
import { Dispatch, RefObject, SetStateAction, useRef, useState } from "react";
import { useRouter } from "next/router";

import { useTranslation } from "../../core/translation.core";
import { CustomerListFilter } from "../../types/customer";
import {
  FancyCheckbox,
  FancyRadiobox2,
  FancyTextInputWithIcon,
  FancyYesNoRadioGroup,
  LabelAndInput,
} from "../reuseables/form.comp";
import XMarkIcon from "../../icons/xMark.icon";
import AdjustmentsHorizontalIcon from "../../icons/adjustmentsHorizontal.icon";
import Modal from "../reuseables/modal.comp";
import MagnifyingIcon from "../../icons/magnifying.icon";
import CloseIcon from "../../icons/iconClose.comp";
import { format } from "date-fns";
import useMemberships from "../../hooks/useMemberships.hook";
import localeCore from "../../core/locale.core";

export default function CustomerListFilterComp({ methods }: { methods: UseFormReturn<CustomerListFilter, any> }) {
  const [modalOpened, setModalOpened] = useState(false);
  const btnRef = useRef<HTMLDivElement>(null);

  return (
    <section data-type="section">
      <FilterBar btnRef={btnRef} setModalOpened={setModalOpened} methods={methods} />
      <FilterModal btnRef={btnRef} modalOpened={modalOpened} setModalOpened={setModalOpened} methods={methods} />
    </section>
  );
}

function FilterModal({
  btnRef,
  modalOpened,
  setModalOpened,
  methods,
}: {
  btnRef: RefObject<HTMLDivElement>;
  modalOpened: boolean;
  setModalOpened: Dispatch<SetStateAction<boolean>>;
  methods: UseFormReturn<CustomerListFilter, any>;
}) {
  const { t, msg } = useTranslation();
  const { push } = useRouter();
  const dummyMethods = useForm<{ search: string }>({
    defaultValues: {
      search: methods.getValues("search"),
    },
  });

  function onSubmitSearch(data: { search: string }) {
    methods.setValue("search", data.search);
    setModalOpened(false);

    if (methods.getValues("page") !== 1) {
      push({
        pathname: "/customers",
        query: { page: 1 },
      });
    }
  }

  if (!modalOpened) return null;

  return (
    <Modal
      id="customer-filter-modal"
      btnRef={btnRef}
      setModalOpened={setModalOpened}
      cssHeight="top-0 md:top-1/2 h-[100dvh] md:h-[700px] "
    >
      <div className="py-14 px-8 space-y-8">
        <h1 className="basis-full text-center text-xl">{t(msg.customer.customers)}</h1>
        <div className="grid grid-cols-2 gap-8">
          <form onSubmit={dummyMethods.handleSubmit(onSubmitSearch)} className="col-span-2 flex gap-2 items-center">
            <FancyTextInputWithIcon
              register={() => dummyMethods.register("search")}
              placeholder={t(msg.customer.searchCustomer)}
              icon={<MagnifyingIcon />}
              rightIcon={<CloseIcon />}
              onClickRightIcon={() => dummyMethods.setValue("search", "")}
              className="grow bg-admin-bg-blue"
            />
            <input type="submit" value={t(msg.form.submit)} className="h-9" />
          </form>

          <div className="block border p-2">
            {t(msg.customer.provider)}
            <FancyCheckbox register={methods.register("providers")} value="local" labelId="customer-provider-local">
              Local
            </FancyCheckbox>
            <FancyCheckbox register={methods.register("providers")} value="facebook" labelId="customer-provider-facebook">
              Facebook
            </FancyCheckbox>
            <FancyCheckbox register={methods.register("providers")} value="google" labelId="customer-provider-google">
              Google
            </FancyCheckbox>
          </div>

          <EmailVerifiedRadio methods={methods} />
          <MobileNumberVerifiedRadio methods={methods} />
          <SuspendedRadio methods={methods} />
          <BlacklistedRadio methods={methods} />
          <MembershipCheckboxes methods={methods} />
          <CreatedDateRange methods={methods} />
        </div>
      </div>
    </Modal>
  );
}

function FilterBar({
  btnRef,
  setModalOpened,
  methods,
}: {
  btnRef: RefObject<HTMLDivElement>;
  setModalOpened: Dispatch<SetStateAction<boolean>>;
  methods: UseFormReturn<CustomerListFilter, any>;
}) {
  return (
    <div
      id="customer-list-filter-bar-btn"
      ref={btnRef}
      className="border rounded-lg py-2 px-4 items-center flex justify-between w-full cursor-pointer"
      onClick={() => setModalOpened(true)}
    >
      <div className="flex flex-wrap gap-2">
        <Search methods={methods} />
        <Providers methods={methods} />
        <EmailVerified methods={methods} />
        <MobileNumberVerified methods={methods} />
        <Suspended methods={methods} />
        <Blacklisted methods={methods} />
        <Memberships methods={methods} />
        <CreatedDateRangeBadge methods={methods} />
      </div>
      <AdjustmentsHorizontalIcon />
    </div>
  );
}

function Search({ methods }: { methods: UseFormReturn<CustomerListFilter, any> }) {
  const search = useWatch({ control: methods.control, name: "search" });

  if (!search) return null;

  return (
    <Badge bgColor="bg-admin-blue" onClose={() => methods.setValue("search", "")}>
      {search}
    </Badge>
  );
}

function Providers({ methods }: { methods: UseFormReturn<CustomerListFilter, any> }) {
  const { t, msg } = useTranslation();
  const providers = useWatch({ control: methods.control, name: "providers" });

  if (!providers) return null;
  if (providers.length === 0) return null;

  const providerLabels: { [key: string]: string } = {
    local: "Local",
    facebook: "Facebook",
    google: "Google",
  };

  return (
    <>
      {providers.map((provider) => (
        <Badge
          bgColor="bg-admin-green"
          key={provider}
          onClose={() => {
            methods.setValue(
              "providers",
              providers.filter((v) => v !== provider)
            );
          }}
        >
          {providerLabels[provider] || provider}
        </Badge>
      ))}
    </>
  );
}

function EmailVerified({ methods }: { methods: UseFormReturn<CustomerListFilter, any> }) {
  const { t, msg } = useTranslation();
  const emailVerified = useWatch({ control: methods.control, name: "emailVerified" });

  if (emailVerified === undefined) return null;

  return (
    <Badge
      bgColor="bg-admin-blue"
      onClose={() => {
        methods.setValue("emailVerified", undefined);
      }}
      dataValue="emailVerified"
    >
      {t(msg.customer.emailVerified)}: {emailVerified ? t(msg.general.yes) : t(msg.general.no)}
    </Badge>
  );
}

function MobileNumberVerified({ methods }: { methods: UseFormReturn<CustomerListFilter, any> }) {
  const { t, msg } = useTranslation();
  const mobileNumberVerified = useWatch({ control: methods.control, name: "mobileNumberVerified" });

  if (mobileNumberVerified === undefined) return null;

  return (
    <Badge
      bgColor="bg-admin-blue"
      onClose={() => {
        methods.setValue("mobileNumberVerified", undefined);
      }}
      dataValue="mobileNumberVerified"
    >
      {t(msg.customer.mobileNumberVerified)}: {mobileNumberVerified ? t(msg.general.yes) : t(msg.general.no)}
    </Badge>
  );
}

function Suspended({ methods }: { methods: UseFormReturn<CustomerListFilter, any> }) {
  const { t, msg } = useTranslation();
  const suspended = useWatch({ control: methods.control, name: "suspended" });

  if (suspended === undefined) return null;

  return (
    <Badge
      bgColor="bg-admin-blue"
      onClose={() => {
        methods.setValue("suspended", undefined);
      }}
    >
      {t(msg.customer.suspended)}: {suspended ? t(msg.general.yes) : t(msg.general.no)}
    </Badge>
  );
}

function Blacklisted({ methods }: { methods: UseFormReturn<CustomerListFilter, any> }) {
  const { t, msg } = useTranslation();
  const blacklisted = useWatch({ control: methods.control, name: "blacklisted" });

  if (blacklisted === undefined) return null;

  return (
    <Badge
      bgColor="bg-admin-blue"
      onClose={() => {
        methods.setValue("blacklisted", undefined);
      }}
    >
      {t(msg.customer.blacklisted)}: {blacklisted ? t(msg.general.yes) : t(msg.general.no)}
    </Badge>
  );
}

function EmailVerifiedRadio({ methods }: { methods: UseFormReturn<CustomerListFilter, any> }) {
  const { t, msg } = useTranslation();

  return (
    <div className="block border p-2">
      <div className="mb-2">{t(msg.customer.emailVerified)}</div>

      <FancyYesNoRadioGroup name="emailVerified" methods={methods} />
    </div>
  );
}

function MobileNumberVerifiedRadio({ methods }: { methods: UseFormReturn<CustomerListFilter, any> }) {
  const { t, msg } = useTranslation();

  return (
    <div className="block border p-2">
      <div className="mb-2">{t(msg.customer.mobileNumberVerified)}</div>
      <FancyYesNoRadioGroup name="mobileNumberVerified" methods={methods} />
    </div>
  );
}

function SuspendedRadio({ methods }: { methods: UseFormReturn<CustomerListFilter, any> }) {
  const { t, msg } = useTranslation();

  return (
    <div className="block border p-2">
      <div className="mb-2">{t(msg.customer.suspended)}</div>
      <FancyYesNoRadioGroup name="suspended" methods={methods} />
    </div>
  );
}

function BlacklistedRadio({ methods }: { methods: UseFormReturn<CustomerListFilter, any> }) {
  const { t, msg } = useTranslation();

  return (
    <div className="block border p-2">
      <div className="mb-2">{t(msg.customer.blacklisted)}</div>
      <FancyYesNoRadioGroup name="blacklisted" methods={methods} />
    </div>
  );
}

function MembershipCheckboxes({ methods }: { methods: UseFormReturn<CustomerListFilter, any> }) {
  const { t, msg } = useTranslation();
  const { data: memberships, isFetched } = useMemberships();

  if (!isFetched || !memberships || memberships.length === 0) return null;

  return (
    <div className="block border p-2">
      <div className="mb-2">{t(msg.membership.membership)}</div>
      {memberships.map((membership) => (
        <FancyCheckbox
          key={membership.id}
          register={methods.register("membershipIds")}
          value={membership.id}
          labelId={`customer-membership-${membership.id}`}
        >
          {localeCore.getLocaleValueDefaultFallback(membership.name)}
        </FancyCheckbox>
      ))}
    </div>
  );
}

function Memberships({ methods }: { methods: UseFormReturn<CustomerListFilter, any> }) {
  const { t, msg } = useTranslation();
  const { data: memberships } = useMemberships();
  const membershipIds = useWatch({ control: methods.control, name: "membershipIds" });

  if (!membershipIds || membershipIds.length === 0) return null;
  if (!memberships) return null;

  const membershipMap = new Map(memberships.map((m) => [m.id, m]));

  return (
    <>
      {membershipIds.map((membershipId) => {
        const membership = membershipMap.get(membershipId);
        if (!membership) return null;

        return (
          <Badge
            bgColor="bg-admin-green"
            key={membershipId}
            onClose={() => {
              methods.setValue(
                "membershipIds",
                membershipIds.filter((id) => id !== membershipId)
              );
            }}
          >
            {localeCore.getLocaleValueDefaultFallback(membership.name)}
          </Badge>
        );
      })}
    </>
  );
}

function CreatedDateRange({ methods }: { methods: UseFormReturn<CustomerListFilter, any> }) {
  const { t, msg } = useTranslation();

  return (
    <div className="block border p-2 col-span-2">
      <div className="mb-2">{t(msg.import.createdAt)}</div>
      <div className="grid grid-cols-2 gap-4">
        <LabelAndInput label={t(msg.formLabels.selectDateFrom)} type="date" register={methods.register} fieldName="dateFrom" />
        <LabelAndInput label={t(msg.formLabels.selectDateTo)} type="date" register={methods.register} fieldName="dateTo" />
      </div>
    </div>
  );
}

function CreatedDateRangeBadge({ methods }: { methods: UseFormReturn<CustomerListFilter, any> }) {
  const { t, msg } = useTranslation();
  const dateFrom = useWatch({ control: methods.control, name: "dateFrom" });
  const dateTo = useWatch({ control: methods.control, name: "dateTo" });

  if (!dateFrom && !dateTo) return null;

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "yyyy-MM-dd");
    } catch {
      return dateStr;
    }
  };

  const displayText =
    dateFrom && dateTo
      ? `${formatDate(dateFrom)} - ${formatDate(dateTo)}`
      : dateFrom
      ? `${t(msg.formLabels.selectDateFrom)}: ${formatDate(dateFrom)}`
      : dateTo
      ? `${t(msg.formLabels.selectDateTo)}: ${formatDate(dateTo)}`
      : "";

  return (
    <Badge
      bgColor="bg-admin-blue"
      onClose={() => {
        methods.setValue("dateFrom", undefined);
        methods.setValue("dateTo", undefined);
      }}
    >
      {t(msg.import.createdAt)}: {displayText}
    </Badge>
  );
}

function Badge({
  bgColor,
  children,
  onClose,
  dataValue,
}: {
  bgColor: string;
  children: React.ReactNode;
  onClose?: () => void;
  dataValue?: string | number;
}) {
  return (
    <div className={`${bgColor} text-white px-4 py-2 rounded-full inline-flex gap-2`} data-value={dataValue}>
      {children}
      {onClose && (
        <button
          className="fill-white"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
